/**
 * MySubmissionsPage — learner view of their own capstone/project submissions.
 *
 * Fetches GET /scenario-os/coach/rubrics/submissions/my and shows:
 *  - Submission date, scene, domain, status
 *  - AI score and grade level if graded
 *  - "View Feedback" button that opens a detail modal
 */

import React, { useState, useEffect, useCallback } from 'react';
import LearnerNav from '../../components/nav/LearnerNav';

function getToken() {
  return localStorage.getItem('scenario_os_token');
}

const STATUS_CONFIG = {
  pending:               { label: 'Queued for AI Grading', bg: '#fef3c7', fg: '#92400e' },
  pending_ai_grade:      { label: 'AI Grading in Progress', bg: '#fef3c7', fg: '#92400e' },
  ai_graded:             { label: 'AI Graded — Passed',  bg: '#dbeafe', fg: '#1e40af' },
  ai_failed:             { label: 'Below Pass — Resubmit', bg: '#fee2e2', fg: '#991b1b' },
  grading_failed:        { label: 'Grading Unavailable', bg: '#fee2e2', fg: '#991b1b' },
  coach_review_pending:  { label: 'Pending Coach Review', bg: '#e0f2fe', fg: '#0369a1' },
  coach_approved:        { label: 'Coach Approved',     bg: '#dcfce7', fg: '#166534' },
  resubmit_requested:    { label: 'Revision Requested', bg: '#ffedd5', fg: '#c2410c' },
  coach_reviewed:        { label: 'Coach Reviewed',     bg: '#dcfce7', fg: '#166534' },
  reviewed:              { label: 'Reviewed',           bg: '#dcfce7', fg: '#166534' },
};

function statusBadge(status) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      display: 'inline-block',
      background: c.bg, color: c.fg,
      fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 999,
      textTransform: 'capitalize', letterSpacing: 0.3,
    }}>
      {c.label}
    </span>
  );
}

function gradeLabel(pct) {
  if (pct == null) return null;
  if (pct >= 90) return { label: 'Excellent', color: '#16a34a' };
  if (pct >= 75) return { label: 'Good',      color: '#2563eb' };
  if (pct >= 60) return { label: 'Satisfactory', color: '#d97706' };
  return            { label: 'Needs Work',   color: '#dc2626' };
}

// ── Detail Modal ────────────────────────────────────────────────────────────

function FeedbackModal({ submissionId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = getToken();
    fetch(`/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submissionId)}/feedback`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(d)))
      .then((data) => setDetail(data))
      .catch((e) => setError(e.detail || e.message || 'Failed to load details.'))
      .finally(() => setLoading(false));
  }, [submissionId]);

  return (
    <>
      <div onClick={onClose} style={overlayStyle} />
      <div style={modalStyle} role="dialog" aria-modal="true" aria-label="Submission Feedback">
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1E1E24' }}>
            Submission Feedback
          </h3>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">✕</button>
        </div>

        <div style={modalBodyStyle}>
          {loading && <div style={{ color: '#6a6a72', padding: '24px 0', textAlign: 'center' }}>Loading…</div>}
          {error && <div style={errorStyle}>{error}</div>}

          {detail && (
            <>
              {/* Meta */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 13, color: '#6a6a72' }}>
                <span>Scene: <strong style={{ color: '#1E1E24' }}>{detail.scene_title || detail.scene_id}</strong></span>
                <span>Domain: <strong style={{ color: '#1E1E24' }}>{detail.domain}</strong></span>
                {detail.submitted_at && (
                  <span>Submitted: <strong style={{ color: '#1E1E24' }}>{new Date(detail.submitted_at).toLocaleDateString()}</strong></span>
                )}
                <span>{statusBadge(detail.status)}</span>
              </div>

              {/* Score summary */}
              {detail.ai_overall_pct != null && (
                <div style={scoreSummaryStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#92140C' }}>
                    {Math.round(detail.ai_overall_pct)}%
                  </div>
                  {gradeLabel(detail.ai_overall_pct) && (
                    <div style={{ fontSize: 15, fontWeight: 700, color: gradeLabel(detail.ai_overall_pct).color, marginTop: 2 }}>
                      {gradeLabel(detail.ai_overall_pct).label}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#6a6a72', marginTop: 2 }}>AI Score</div>
                </div>
              )}

              {/* Always show the learner's original submission */}
              <div style={{ marginBottom: 16 }}>
                <div style={sectionLabelStyle}>Your Submission</div>
                {detail.text ? (
                  <div style={submissionBoxStyle}>{detail.text}</div>
                ) : (
                  <div style={{ ...submissionBoxStyle, color: '#6a6a72', fontStyle: 'italic' }}>
                    No written submission text was stored for this attempt.
                    {detail.file_url ? (
                      <>
                        {' '}
                        <a href={detail.file_url} target="_blank" rel="noreferrer">Open attached file</a>
                      </>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Per-criterion scores */}
              {(detail.scores || detail.ai_scores || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabelStyle}>Criterion Scores</div>
                  {(detail.scores || detail.ai_scores).map((s, i) => (
                    <div key={i} style={criterionRowStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#1E1E24' }}>{s.criterion_name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#92140C' }}>
                          {s.score}{s.max_score != null ? ` / ${s.max_score}` : ''}
                        </span>
                      </div>
                      {(s.feedback || s.reasoning) && (
                        <p style={{ margin: 0, fontSize: 12, color: '#6a6a72', lineHeight: 1.5 }}>
                          {s.feedback || s.reasoning}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* AI overall feedback — strengths / improvements / encouragement */}
              {(detail.ai_feedback_text || detail.ai_feedback) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabelStyle}>AI Feedback</div>
                  <div style={{ ...submissionBoxStyle, background: '#f0f9ff', borderColor: '#bae6fd', color: '#0c4a6e', whiteSpace: 'pre-wrap' }}>
                    {detail.ai_feedback_text
                      || (typeof detail.ai_feedback === 'string'
                        ? detail.ai_feedback
                        : [
                            detail.ai_feedback?.strengths && `Strengths: ${detail.ai_feedback.strengths}`,
                            detail.ai_feedback?.improvements && `Areas for improvement: ${detail.ai_feedback.improvements}`,
                            detail.ai_feedback?.encouragement,
                          ].filter(Boolean).join('\n\n'))}
                  </div>
                </div>
              )}

              {/* Coach feedback (2-level approve / resubmit / manual grade) */}
              {(detail.coach_feedback || detail.overall_feedback || detail.coach_note) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabelStyle}>Coach Feedback</div>
                  <div style={{ ...submissionBoxStyle, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
                    {detail.coach_feedback || detail.overall_feedback || detail.coach_note}
                  </div>
                </div>
              )}

              {detail.status === 'coach_review_pending' && (
                <div style={{ textAlign: 'center', color: '#0369a1', padding: '12px 0', fontSize: 14 }}>
                  Your AI-graded submission is awaiting coach review.
                </div>
              )}

              {['pending', 'pending_ai_grade'].includes(detail.status) && (
                <div style={{ textAlign: 'center', color: '#92400e', padding: '24px 0', fontSize: 14 }}>
                  AI grading is in progress. Scores appear here when the grading agent finishes —
                  no placeholder scores are shown while the agent is unavailable.
                </div>
              )}

              {detail.status === 'ai_failed' && (
                <div style={{ textAlign: 'center', color: '#991b1b', padding: '12px 0', fontSize: 14 }}>
                  This attempt scored below the pass threshold. Open the project scene to resubmit.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function MySubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const res = await fetch('/scenario-os/coach/rubrics/submissions', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch (e) {
      setError(e.message || 'Failed to load submissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <LearnerNav />

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 20px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1E1E24', margin: '0 0 6px' }}>
          My Submissions
        </h2>
        <p style={{ color: '#6a6a72', fontSize: 14, margin: '0 0 28px' }}>
          Your project and capstone submissions, AI grades, and coach feedback.
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6a6a72' }}>Loading your submissions…</div>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        {!loading && !error && submissions.length === 0 && (
          <div style={emptyStyle}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1E1E24', marginBottom: 6 }}>
              No submissions yet
            </div>
            <div style={{ fontSize: 14, color: '#6a6a72' }}>
              Complete a capstone or project scene to see your submissions here.
            </div>
          </div>
        )}

        {!loading && !error && submissions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {submissions.map((sub) => {
              const grade = gradeLabel(sub.ai_overall_pct);
              return (
                <div key={sub.submission_id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#1E1E24', marginBottom: 4 }}>
                        {sub.scene_title || sub.scene_id || 'Untitled Scene'}
                      </div>
                      <div style={{ fontSize: 13, color: '#6a6a72' }}>
                        {sub.domain && <span>Domain: <strong>{sub.domain}</strong> · </span>}
                        {sub.submitted_at && (
                          <span>Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      {statusBadge(sub.status)}
                      {sub.ai_overall_pct != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 20, fontWeight: 800, color: '#92140C' }}>
                            {Math.round(sub.ai_overall_pct)}%
                          </span>
                          {grade && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: grade.color }}>
                              {grade.label}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {sub.text && (
                    <p style={{ margin: '10px 0 0', fontSize: 13, color: '#6a6a72', lineHeight: 1.5 }}>
                      {sub.text.slice(0, 150)}{sub.text.length > 150 ? '…' : ''}
                    </p>
                  )}

                  <div style={{ marginTop: 14 }}>
                    <button
                      onClick={() => setSelectedId(sub.submission_id)}
                      style={viewBtnStyle}
                    >
                      View Feedback →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedId && (
        <FeedbackModal
          submissionId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const errorStyle = {
  background: 'rgba(220,38,38,0.08)',
  border: '1px solid rgba(220,38,38,0.25)',
  borderRadius: 8,
  color: '#b91c1c',
  padding: '12px 16px',
  fontSize: 14,
  marginBottom: 16,
};

const emptyStyle = {
  textAlign: 'center',
  padding: '60px 24px',
  background: '#fff',
  borderRadius: 12,
  border: '1px dashed #FFCF99',
};

const cardStyle = {
  background: '#fff',
  border: '1px solid #FFCF99',
  borderRadius: 12,
  padding: '18px 20px',
  boxShadow: '0 2px 8px rgba(30,30,36,0.06)',
};

const viewBtnStyle = {
  background: '#92140C',
  color: '#FFF8F0',
  border: 'none',
  borderRadius: 6,
  padding: '7px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 200,
};

const modalStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 640,
  maxHeight: '85vh',
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 201,
};

const modalHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid #FFCF99',
};

const closeBtnStyle = {
  background: 'transparent',
  border: 'none',
  fontSize: 18,
  color: '#6a6a72',
  cursor: 'pointer',
  padding: '4px 8px',
};

const modalBodyStyle = {
  padding: '20px',
  overflowY: 'auto',
  flex: 1,
};

const scoreSummaryStyle = {
  textAlign: 'center',
  background: 'rgba(146,20,12,0.06)',
  border: '1px solid rgba(146,20,12,0.15)',
  borderRadius: 10,
  padding: '16px',
  marginBottom: 20,
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#6a6a72',
  marginBottom: 8,
};

const submissionBoxStyle = {
  background: '#FFF8F0',
  border: '1px solid rgba(255,207,153,0.5)',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: 13,
  color: '#1E1E24',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const criterionRowStyle = {
  background: '#FFF8F0',
  border: '1px solid rgba(255,207,153,0.4)',
  borderRadius: 8,
  padding: '10px 14px',
  marginBottom: 8,
};
